import { getBusiness, getType, TYPE_QUESTIONS } from "@/data/business";

const liveProxy = (getter) => new Proxy({}, {
  get(_, prop) {
    const target = getter();
    const v = target[prop];
    return typeof v === "function" ? v.bind(target) : v;
  },
  has(_, prop) { return prop in getter(); },
  ownKeys() { return Reflect.ownKeys(getter()); },
  getOwnPropertyDescriptor(_, prop) { return Object.getOwnPropertyDescriptor(getter(), prop); },
});

export const CASALE = liveProxy(getBusiness);

const BASE_SECTIONS = [
  {
    id: "struttura",
    title: "La struttura oggi",
    intro: "Partiamo da come lavora l'attività adesso: numeri, spazi e abitudini.",
    questions: [
      { id: "q1", type: "number", unit: "eventi all'anno", placeholder: "es. 60",
        text: "Quanti eventi ospitate in un anno, più o meno?" },
      { id: "q2", type: "single", text: "Qual è la tipologia di evento più frequente?",
        options: ["Matrimoni", "Banchetti e cerimonie (battesimi, comunioni, anniversari)", "Compleanni e feste private", "Cene aziendali", "Un mix senza una tipologia dominante"] },
      { id: "q3", type: "multiple", text: "Quali spazi usate davvero durante gli eventi?",
        hint: "Potete scegliere più risposte.",
        options: ["Ingresso di rappresentanza", "Sala interna", "Prato per eventi all'aperto", "Piscina", "Le 3 camere in affitto"] },
      { id: "q4", type: "text", placeholder: "Scrivete liberamente, anche poche righe.",
        text: "Cosa funziona meglio oggi, e cosa invece vi fa perdere il sonno?" },
    ],
  },
  {
    id: "clienti",
    title: "Clienti e mercato",
    intro: "Chi vi sceglie, come vi trova e con chi vi confrontate.",
    questions: [
      { id: "q5", type: "multiple", text: "Chi sono i vostri clienti tipo?",
        hint: "Potete scegliere più risposte.",
        options: ["Coppie che si sposano", "Famiglie per cerimonie", "Aziende", "Gruppi di amici e feste private", "Ospiti delle camere"] },
      { id: "q6", type: "multiple", text: "Da dove arriva la maggior parte delle richieste?",
        hint: "Potete scegliere più risposte.",
        options: ["Passaparola", "Google e sito web", "Instagram / Facebook", "Wedding planner e agenzie", "Portali (Matrimonio.com, Zankyou…)", "Clienti che tornano"] },
      { id: "q7", type: "text", placeholder: "Nomi di locali o semplicemente il tipo di attività.",
        text: "Quali concorrenti vi vengono in mente, e cosa hanno in più o in meno rispetto a voi?" },
    ],
  },
  {
    id: "organizzazione",
    title: "Organizzazione e persone",
    intro: "Chi fa cosa, come si coordina un evento e dove si inceppa la macchina.",
    questions: [
      { id: "q8", type: "number", unit: "persone fisse", placeholder: "es. 6",
        text: "Quante persone lavorano con voi in modo fisso (sala, cucina, pulizie, ufficio)?" },
      { id: "q9", type: "single", text: "Come viene coordinato un evento, dal primo contatto al giorno stesso?",
        options: ["Ce ne occupiamo noi titolari in prima persona", "C'è un responsabile dedicato", "Ci si organizza di volta in volta", "Dipende dal tipo di evento"] },
      { id: "q10", type: "multiple", text: "Dove nascono di solito gli intoppi?",
        hint: "Potete scegliere più risposte.",
        options: ["Comunicazione tra sala e cucina", "Personale a chiamata poco affidabile", "Tempi di servizio", "Preventivi e risposte ai clienti", "Gestione del cliente il giorno dell'evento", "Fornitori", "Allestimenti e pulizie", "Nulla di particolare"] },
    ],
  },
  {
    id: "cucina",
    title: "Cucina e transizione",
    intro: "Chi guida la cucina oggi, cosa deve restare com'è e cosa cercate per domani.",
    questions: [
      { id: "q11", type: "single", text: "Qual è oggi la situazione dello chef?",
        options: ["Ha già lasciato", "Lascia entro un mese", "Lascia entro tre mesi", "Non ci sono ancora date certe", "Lo chef resta, cerchiamo solo supporto"] },
      { id: "q12", type: "scale", min: 1, max: 5, lowLabel: "Per niente", highLabel: "Del tutto",
        showIf: (a) => a.q11 !== "Lo chef resta, cerchiamo solo supporto",
        text: "Quanto siete aperti a una gestione temporanea della cucina da parte del consulente, finché non arriva il nuovo chef?" },
      { id: "q13", type: "text", placeholder: "Piatti simbolo, prodotti, impostazione del menù, stile di servizio…",
        text: "Cosa non deve cambiare nel menù e nello stile dell'attività?" },
      { id: "q14", type: "multiple", text: "Cosa cercate nel prossimo chef?",
        hint: "Potete scegliere più risposte.",
        showIf: (a) => a.q11 !== "Lo chef resta, cerchiamo solo supporto",
        options: ["Cucina del territorio e tradizione", "Capacità di gestire grandi numeri", "Creatività e menù moderni", "Affidabilità e controllo dei costi", "Capacità di guidare la brigata", "Esperienza nei matrimoni"] },
    ],
  },
  {
    id: "strategia",
    title: "Scelte strategiche",
    intro: "Poche domande secche: servono a capire che tipo di strategia conviene e da dove partire.",
    questions: [
      { id: "q19", type: "single", text: "Come va il calendario degli eventi?",
        options: ["Abbiamo più richieste di quante ne possiamo accettare", "Pieno in alta stagione, vuoto nel resto dell'anno", "C'è spazio libero anche in alta stagione", "Cambia molto da un anno all'altro"] },
      { id: "q20", type: "single", text: "Rispetto ai concorrenti, i vostri prezzi sono…",
        options: ["Più bassi", "In linea", "Più alti", "Non lo sappiamo con precisione"] },
      { id: "q21", type: "single", text: "Alla fine di un evento, sapete quanto ci avete guadagnato davvero?",
        options: ["Sì, con precisione", "Più o meno", "Lo vediamo solo a fine anno", "No"] },
      { id: "q22", type: "scale", min: 1, max: 5, lowLabel: "Per niente", highLabel: "Molto",
        text: "Quanto siete soddisfatti della vostra presenza online (sito, social, recensioni)?" },
      { id: "q23", type: "single", text: "Se voi titolari mancaste per due settimane, cosa succederebbe?",
        options: ["Si fermerebbe tutto", "Si andrebbe avanti, ma con difficoltà", "Funzionerebbe quasi normalmente"] },
      { id: "q24", type: "multiple", text: "Quali di questi strumenti usate già oggi?",
        hint: "Potete scegliere più risposte.",
        options: ["Sito web aggiornato", "Profili social attivi", "Gestione delle recensioni", "Preventivo scritto standard", "Contratto standard", "Calendario condiviso con lo staff", "Schede costi per evento o menù", "Nessuno di questi"] },
      { id: "q25", type: "single", text: "Quanto siete disposti a cambiare?",
        options: ["Solo ritocchi: l'impostazione va bene così", "Cambiamenti decisi, ma graduali", "Pronti a stravolgere ciò che non funziona"] },
    ],
  },
  {
    id: "obiettivi",
    title: "Obiettivi e aspettative",
    intro: "Dove volete arrivare nei prossimi dodici mesi e cosa vi aspettate da questo percorso.",
    questions: [
      { id: "q15", type: "text", placeholder: "Più eventi, più margine, meno fatica, una squadra autonoma…",
        text: "Cosa vorreste ottenere, concretamente, nei prossimi 12 mesi?" },
      { id: "q16", type: "single", text: "Su cosa dovrebbe concentrarsi per primo l'intervento?",
        options: ["Marketing e presenza online", "Organizzazione interna e personale", "Strategia: prezzi, posizionamento, crescita", "Cucina e menù", "Un po' di tutto, serve una regia"] },
      { id: "q17", type: "single", text: "Quanto tempo potete dedicare personalmente al percorso?",
        options: ["Poche ore al mese: preferiamo delegare", "Un incontro a settimana", "Vogliamo essere coinvolti ogni giorno", "Dipende dalla stagione"] },
      { id: "q18", type: "single", text: "Che budget indicativo immaginate per questo percorso nel primo anno?",
        options: ["Fino a 5.000 €", "Tra 5.000 e 15.000 €", "Oltre 15.000 €", "Preferiamo definirlo insieme"] },
    ],
  },
];

export const getSections = (typeId, business) => {
  const b = business || getBusiness();
  const t = getType(typeId || b.type);
  const spaces = typeId && typeId !== b.type ? t.spaces : b.spaces || t.spaces;
  const over = {
    q1: { ...t.q1 },
    q2: { ...t.q2 },
    q3: { text: t.q3.text, options: spaces },
    q5: { options: t.q5 },
    q6: { options: t.q6 },
    q10: { options: t.q10 },
    q19: { ...t.q19 },
  };
  const sections = BASE_SECTIONS.map((s) => ({
    ...s,
    intro: s.id === "struttura" ? `Partiamo da come lavora ${t.short} adesso: numeri, spazi e abitudini.` : s.intro,
    questions: s.questions.map((q) => (over[q.id] ? { ...q, ...over[q.id] } : q)),
  }));
  const dedicated = (TYPE_QUESTIONS[t.id] || []).map((q, i) => ({ ...q, id: `qt${i + 1}` }));
  if (dedicated.length) sections.splice(1, 0, { id: "specifico", title: `Domande specifiche: ${t.label}`, intro: `Tre domande pensate apposta per ${t.short}: servono a capire i punti che contano in questo tipo di attività.`, questions: dedicated });
  return sections;
};

export const SECTIONS = liveProxy(() => getSections());

export const isVisible = (q, answers) => !q.showIf || q.showIf(answers);

export const visibleQuestions = (section, answers) => section.questions.filter((q) => isVisible(q, answers));

export const formatAnswer = (q, value) => {
  if (value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0)) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (q.type === "scale") return `${value} su ${q.max}`;
  if (q.type === "number") return `${value} ${q.unit}`;
  return String(value);
};

export const toReportPayload = (submission, priorities = "") => ({
  respondent: submission.respondent,
  priorities,
  answers: getSections(submission.businessType).flatMap((s) =>
    visibleQuestions(s, submission.answers).map((q) => ({
      section: s.title,
      question: q.text,
      answer: formatAnswer(q, submission.answers[q.id]),
    }))
  ),
});
